import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, tap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { API_ENDPOINTS } from '../constants/api-endpoints.constants';
import { ApiResponse } from '../models/api-response.model';

export interface AdminUserDto {
  id: string;
  name: string;
  email: string;
  phone: string;

  countryCode?: string;
  countryName?: string;

  address: string;
  profileImageUrl?: string | null;

  autoGeneratePassword?: boolean;
  resetPasswordFirstLogin?: boolean;

  status: string;
  enabled?: boolean;
  passwordResetRequired?: boolean;

  roleIds: string[];
  roleNames: string[];

  effectiveRoleId?: string | null;
  effectiveRoleName?: string | null;
  effectiveRoleKey?: string | null;
  effectiveHierarchyLevel?: number | null;

  directManagerUserId?: string | null;
  directManagerName?: string | null;
  directManagerEmail?: string | null;
  directManagerRoleName?: string | null;
  directManagerRoleKey?: string | null;

  createdAt?: string;
  updatedAt?: string;

  isLocked: boolean;
}

export interface UsersResponse {
  content: AdminUserDto[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export interface UsersQueryParams {
  search?: string;
  name?: string;
  email?: string;
  phone?: string;
  status?: string;
  sort?: string;
  page?: number;
  size?: number;
}

export interface AdminUserPayload {
  id?: string;
  name: string;
  email: string;
  phone: string;
  countryCode?: string;
  countryName?: string;
  address?: string;
  status: string;
  roleIds: string[];
  directManagerUserId?: string | null;
  profileImageUrl?: string;
  removeProfileImage?: boolean;
  password?: string;
  autoGeneratePassword: boolean;
  resetPasswordFirstLogin: boolean;
}

export interface UpdateProfilePayload {
  name: string;
  phone: string;

  countryCode?: string;
  countryName?: string;
  address?: string;

  profileImageUrl?: string;
  removeProfileImage?: boolean;
}

export interface CreatableRoleDto {
  roleId: string;
  roleName: string;
  roleKey?: string;
  hierarchyLevel?: number;
}

export interface UserManagerOptionDto {
  userId: string;
  userName: string;
  email?: string | null;

  roleId: string;
  roleName: string;
  roleKey: string;
  hierarchyLevel: number;

  displayName?: string;
}

export interface UserManagerSelectionDto {
  targetRoleId: string;
  targetRoleName: string;
  targetRoleKey: string;
  targetHierarchyLevel: number;

  requiredManagerLevel?: number | null;

  managerRequired: boolean;
  autoSelectedManagerUserId?: string | null;

  managers: UserManagerOptionDto[];
}

@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private readonly currentProfileSubject =
    new BehaviorSubject<AdminUserDto | null>(null);

  readonly currentProfile$ =
    this.currentProfileSubject.asObservable();

  constructor(private http: HttpClient) { }

  getUsers(
    params: UsersQueryParams = {}
  ): Observable<ApiResponse<UsersResponse>> {
    let httpParams = new HttpParams();

    const values: Record<string, string | number | undefined> = {
      search: params.search,
      name: params.name,
      email: params.email,
      phone: params.phone,
      status: params.status
        ? this.toBackendStatus(params.status)
        : undefined,
      page: params.page !== undefined
        ? params.page - 1
        : undefined,
      size: params.size,
      sort: params.sort
    };

    Object.entries(values).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return this.http.get<ApiResponse<UsersResponse>>(
      API_ENDPOINTS.USERS.LIST,
      { params: httpParams }
    );
  }

  getUserById(
    id: string
  ): Observable<ApiResponse<AdminUserDto>> {
    return this.http.get<ApiResponse<AdminUserDto>>(
      API_ENDPOINTS.USERS.DETAILS(id)
    );
  }

  createUser(
    payload: AdminUserPayload
  ): Observable<ApiResponse<AdminUserDto>> {
    return this.http.post<ApiResponse<AdminUserDto>>(
      API_ENDPOINTS.USERS.CREATE,
      payload
    );
  }

  updateUser(
    id: string,
    payload: AdminUserPayload
  ): Observable<ApiResponse<AdminUserDto>> {
    return this.http.put<ApiResponse<AdminUserDto>>(
      API_ENDPOINTS.USERS.UPDATE(id),
      payload
    );
  }

  saveUser(
    payload: AdminUserPayload
  ): Observable<ApiResponse<AdminUserDto>> {
    return payload.id
      ? this.updateUser(payload.id, payload)
      : this.createUser(payload);
  }

  getCreatableRoles():
    Observable<ApiResponse<CreatableRoleDto[]>> {
    return this.http.get<ApiResponse<CreatableRoleDto[]>>(
      API_ENDPOINTS.ROLES.CREATEBLE_ROLELIST
    );
  }

  getCreatebleRoles():
    Observable<ApiResponse<CreatableRoleDto[]>> {
    return this.getCreatableRoles();
  }

  getManagerOptions(
    roleIds: string[],
    editedUserId?: string | null
  ): Observable<ApiResponse<UserManagerSelectionDto>> {
    let params = new HttpParams();

    roleIds.forEach(roleId => {
      params = params.append('roleIds', roleId);
    });

    if (editedUserId) {
      params = params.set('editedUserId', editedUserId);
    }

    return this.http.get<
      ApiResponse<UserManagerSelectionDto>
    >(
      API_ENDPOINTS.USERS.MANAGER_OPTIONS,
      { params }
    );
  }

  updateUserStatus(
    id: string,
    status: 'ACTIVE' | 'INACTIVE'
  ): Observable<ApiResponse<AdminUserDto>> {
    const params = new HttpParams().set(
      'status',
      status
    );

    return this.http.patch<ApiResponse<AdminUserDto>>(
      API_ENDPOINTS.USERS.STATUS(id),
      null,
      { params }
    );
  }

  deleteUser(
    id: string
  ): Observable<ApiResponse<string>> {
    return this.http.delete<ApiResponse<string>>(
      API_ENDPOINTS.USERS.DELETE(id)
    );
  }

  deleteMultipleUsers(
    ids: string[]
  ): Observable<ApiResponse<string>> {
    return this.http.delete<ApiResponse<string>>(
      API_ENDPOINTS.USERS.DELETE_MULTIPLE,
      { body: ids }
    );
  }

  getMyProfile():
    Observable<ApiResponse<AdminUserDto>> {
    return this.http.get<ApiResponse<AdminUserDto>>(
      API_ENDPOINTS.USERS.PROFILE_ME
    ).pipe(
      tap(response => {
        if (response?.data) {
          this.currentProfileSubject.next(response.data);
        }
      })
    );
  }

  updateMyProfile(
    payload: UpdateProfilePayload
  ): Observable<ApiResponse<AdminUserDto>> {
    return this.http.put<ApiResponse<AdminUserDto>>(
      API_ENDPOINTS.USERS.PROFILE_ME,
      payload
    ).pipe(
      tap(response => {
        if (response?.data) {
          this.currentProfileSubject.next(response.data);
        }
      })
    );
  }

  getCurrentProfileSnapshot(): AdminUserDto | null {
    return this.currentProfileSubject.value;
  }

  setCurrentProfile(profile: AdminUserDto): void {
    this.currentProfileSubject.next(profile);
  }

  clearCurrentProfile(): void {
    this.currentProfileSubject.next(null);
  }

  toUiStatus(
    status?: string | null
  ): 'Active' | 'Inactive' {
    return status?.toUpperCase() === 'ACTIVE'
      ? 'Active'
      : 'Inactive';
  }

  toBackendStatus(
    status?: string | null
  ): string {
    if (!status) return '';
    if (status === 'Active') return 'ACTIVE';
    if (status === 'Inactive') return 'INACTIVE';

    return status.toUpperCase();
  }

  /**
   * Bulk-resolves user ids to display names (e.g. a candidate's createdBy
   * attribution). Mirrors JobService.resolveTitles's shape: dedupe/trim ids,
   * short-circuit to {} when empty, swallow errors into {} rather than
   * breaking the caller's list rendering.
   */
  resolveNames(userIds: string[]): Observable<Record<string, string>> {
    const uniqueIds = Array.from(
      new Set((userIds || []).map((id) => (id || '').trim()).filter((id) => !!id))
    );

    if (uniqueIds.length === 0) {
      return of({});
    }

    let params = new HttpParams();
    uniqueIds.forEach((id) => {
      params = params.append('ids', id);
    });

    return this.http
      .get<ApiResponse<Record<string, string>>>(API_ENDPOINTS.USERS.NAMES, { params })
      .pipe(
        map((res) => res.data || {}),
        catchError(() => of({} as Record<string, string>))
      );
  }
}